import { Component, OnInit, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MediaService } from '@exl-ng/mulo-core';
import { BehaviorSubject, Observable } from 'rxjs';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import {
    PortalHomepageSections,
    TopTenOption,
} from '../../shared/configurations/portal-homepage.constant';
import {
    Asset,
    AssetsSearchResults,
} from '../../shared/interfaces/assets-search-results.interface';
import { Author } from '../../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-relations/author.interface';
import { HomepageSectionConfiguration } from '../../shared/interfaces/research-portal/portal-configuration/homepage-configuration.interface';
import { UrlUtils } from '../../shared/utils/url.utils';
import { PortalService } from '../portal.service';
import { TranslateModule } from '@ngx-translate/core';
import { ExpandableComponent, HtmlSanitizePipe } from '@exl-ng/mulo-common';
import { CitationBriefComponent } from '../../parts/citations/citation-brief/citation-brief.component';
import { AsyncPipe } from '@angular/common';
import { SwiperModule } from 'swiper/angular';
import { AriaSwiperDirective } from '../../shared/directives/a11y/aria-swiper.directive';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { CenteredBlockLayoutComponent } from '@exl-ng/mulo-views';

SwiperCore.use([Navigation, Pagination]);

@Component({
    selector: 'esp-portal-featured',
    templateUrl: './portal-featured.component.html',
    styleUrls: ['./portal-featured.component.scss'],
    imports: [
        CenteredBlockLayoutComponent,
        MatTabGroup,
        MatTab,
        AriaSwiperDirective,
        SwiperModule,
        CitationBriefComponent,
        ExpandableComponent,
        AsyncPipe,
        TranslateModule,
        HtmlSanitizePipe
    ]
})
export class PortalFeaturedComponent implements OnInit {
    media = inject(MediaService);
    private portalService = inject(PortalService);
    private sanitizer = inject(DomSanitizer);

    topTenSection: HomepageSectionConfiguration;
    topTenOptions: string[];

    tabOneLabel = '';
    tabTwoLabel = '';
    tabThreeLabel = '';

    tabOneAssets: Asset[] = [];
    tabTwoAssets: Asset[] = [];
    tabThreeAssets: Asset[] = [];

    tabChanged$ = new BehaviorSubject<number>(0);

    institution = UrlUtils.getParam('institution');

    ngOnInit() {
        this.topTenSection = this.portalService.getHomepageSectionByCode(
            PortalHomepageSections.TOP_TEN
        );
        this.topTenOptions = this.topTenSection.topTenOptions;
        this.loadTabOne();
        this.loadTabTwo();
        this.loadTabThree();
    }

    onTabChanged(event) {
        this.tabChanged$.next(event.index);
    }

    loadTabOne() {
        this.tabOneLabel = this.getLabel(this.topTenOptions[0]);
        this.getAssetObservable(this.topTenOptions[0]).subscribe((data) => {
            const assetSearchResults = data as AssetsSearchResults;
            this.tabOneAssets = assetSearchResults.assets;
        });
    }

    loadTabTwo() {
        if (this.topTenOptions.length >= 2) {
            this.tabTwoLabel = this.getLabel(this.topTenOptions[1]);
            this.getAssetObservable(this.topTenOptions[1]).subscribe((data) => {
                const assetSearchResults = data as AssetsSearchResults;
                this.tabTwoAssets = assetSearchResults.assets;
            });
        }
    }

    loadTabThree() {
        if (this.topTenOptions.length >= 3) {
            this.tabThreeLabel = this.getLabel(this.topTenOptions[2]);
            this.getAssetObservable(this.topTenOptions[2]).subscribe((data) => {
                const assetSearchResults = data as AssetsSearchResults;
                this.tabThreeAssets = assetSearchResults.assets;
            });
        }
    }

    getLabel(option) {
        let label = '';
        switch (option) {
            case TopTenOption.NEWEST:
                label = 'research.portal.topten.recently.added';
                break;
            case TopTenOption.MOST_DOWNLOADED:
                label = 'research.portal.topten.most.viewed';
                break;
            case TopTenOption.MOST_METADATA_VIEWED:
                label = 'research.portal.topten.most.mdviewed';
                break;
            case TopTenOption.SET_ONE:
                label = 'research.portal.topten.set.one';
                break;
            case TopTenOption.SET_TWO:
                label = 'research.portal.topten.set.two';
                break;
            case TopTenOption.SET_THREE:
                label = 'research.portal.topten.set.three';
                break;
            default:
                label = '';
        }
        return label;
    }

    getAssetObservable(option) {
        let observable: Observable<Object>;
        switch (option) {
            case TopTenOption.NEWEST:
                observable = this.getAssetsBriefByIdsList(
                    this.topTenSection.newest
                );
                break;
            case TopTenOption.MOST_DOWNLOADED:
                observable = this.getAssetsBriefByIdsList(
                    this.topTenSection.mostViewed
                );
                break;
            case TopTenOption.MOST_METADATA_VIEWED:
                observable = this.getAssetsBriefByIdsList(
                    this.topTenSection.mostMetadataViewed
                );
                break;
            case TopTenOption.SET_ONE:
                observable = this.getAssetsBriefBySetIds(
                    this.topTenSection.setOneId
                );
                break;
            case TopTenOption.SET_TWO:
                observable = this.getAssetsBriefBySetIds(
                    this.topTenSection.setTwoId
                );
                break;
            case TopTenOption.SET_THREE:
                observable = this.getAssetsBriefBySetIds(
                    this.topTenSection.setThreeId
                );
                break;
            default:
                observable = this.getAssetsBriefByIdsList(
                    this.topTenSection.newest
                ); //TODO decide
        }
        return observable;
    }

    getAuthors(creators: Author[]) {
        return creators?.map((creator) => {
            return creator?.displayName;
        });
    }

    getAssetsBriefByIdsList(idsList) {
        return this.portalService.getAssetsBriefByIdsList(idsList);
    }

    getAssetsBriefBySetIds(setId) {
        return this.portalService.getAssetsBriefBySetIds(setId);
    }

    getFullAssetPageQueryParams() {
        return { institution: this.institution };
    }
}
